import React from "react"
import { format, isPast, isToday, isTomorrow } from "date-fns"
import { parseDateOnly } from "@/lib/datetime"
import { AlertTriangle, CheckCircle2, ChevronDown, Circle, Clock, XCircle, Zap } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ActionItemStatus } from "@/lib/types"
import { cn } from "@/lib/utils"

/**
 * How action items look and change status, shared by the Action Items list and
 * the single-item page (/action-items/:id) so the two cannot drift.
 */

export const getPriorityConfig = (priority: string | undefined) => {
  switch (priority) {
    case "URGENT":
      return { icon: AlertTriangle, color: "text-destructive", bg: "bg-destructive/10", label: "Urgent" }
    case "MEDIUM":
      return { icon: Zap, color: "text-warning", bg: "bg-warning/10", label: "Medium" }
    case "LOW":
      return { icon: Circle, color: "text-info", bg: "bg-info/10", label: "Low" }
    default:
      return { icon: Circle, color: "text-muted-foreground", bg: "bg-muted", label: "None" }
  }
}

export const getStatusConfig = (status: string) => {
  switch (status) {
    case "completed":
      return { icon: CheckCircle2, color: "text-success", bg: "bg-success/10", label: "Completed" }
    case "in_progress":
      return { icon: Clock, color: "text-info", bg: "bg-info/10", label: "In Progress" }
    case "cancelled":
      return { icon: XCircle, color: "text-muted-foreground", bg: "bg-muted", label: "Cancelled" }
    default:
      return { icon: Circle, color: "text-muted-foreground", bg: "bg-muted", label: "Not Started" }
  }
}

export const formatDueDate = (dateString?: string | null) => {
  if (!dateString) return null
  const date = parseDateOnly(dateString)
  if (!date) return null
  if (isToday(date)) return { text: "Today", isOverdue: false, isUrgent: true }
  if (isTomorrow(date)) return { text: "Tomorrow", isOverdue: false, isUrgent: true }
  if (isPast(date)) return { text: format(date, "MMM d"), isOverdue: true, isUrgent: false }
  return { text: format(date, "MMM d"), isOverdue: false, isUrgent: false }
}

export const PriorityBadge: React.FC<{ priority?: string }> = ({ priority }) => {
  const config = getPriorityConfig(priority)
  const Icon = config.icon
  return (
    <div className={cn("inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium", config.bg, config.color)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </div>
  )
}

const STATUS_OPTIONS: { value: ActionItemStatus; label: string; icon: typeof Circle; color: string }[] = [
  { value: "pending", label: "Not Started", icon: Circle, color: "text-muted-foreground" },
  { value: "in_progress", label: "In Progress", icon: Clock, color: "text-info" },
  { value: "completed", label: "Completed", icon: CheckCircle2, color: "text-success" },
  { value: "cancelled", label: "Cancelled", icon: XCircle, color: "text-muted-foreground" },
]

/** The status button + menu used wherever an item's status can be changed. */
export const ActionItemStatusMenu: React.FC<{
  status: string
  onChange: (status: ActionItemStatus) => void
  disabled?: boolean
}> = ({ status, onChange, disabled }) => {
  const config = getStatusConfig(status)
  const Icon = config.icon
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn("h-7 gap-1.5 px-2 font-normal", config.color)}
        >
          <Icon className="h-3.5 w-3.5" />
          {config.label}
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {STATUS_OPTIONS.map((option) => {
          const OptionIcon = option.icon
          return (
            <DropdownMenuItem key={option.value} onClick={() => onChange(option.value)}>
              <OptionIcon className={cn("h-4 w-4 mr-2", option.color)} /> {option.label}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const ASSIGNEE_LABEL: Record<string, string> = {
  PEAR_AI: "Pear AI",
  CARE_TEAM: "Care Team",
  ADMIN: "Admin",
}
